import React, { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Container, Col, Row } from 'react-bootstrap';
import hash from 'object-hash';

import Styles from '../Styles';
import SearchBar from '../Parts/SearchBar';
import apiCalls from '../PowerShadesAPIFunctions';
import type { ExistingAddress, RootWithAlert } from './addressTypes';

declare const ROOT: RootWithAlert;

type AddressListResponse = {
	addresses?: ExistingAddress[];
	page_count?: number;
};

type AddressUpdateResponse = {
	address?: ExistingAddress;
};

type ExistingAddressSummaryProps = {
	address: ExistingAddress;
	onUseAddress: (address: ExistingAddress) => void | Promise<void>;
	quoteType: string;
};

type ExistingAddressesProps = {
	quoteType: string;
	getAddressesFunction: string;
	useAddress: (address: ExistingAddress) => void;
	createNewAddress?: () => void;
	fullHeight?: boolean;
};

export type { ExistingAddress } from './addressTypes';

const searchBarStyle: CSSProperties = {
	fontSize: '14px',
};

const ExistingAddressSummary = ({ address, onUseAddress, quoteType }: ExistingAddressSummaryProps) => {
	const cityString = (address.city ?? '')
		+ ((address.state ?? '').length ? `, ${address.state}` : '')
		+ ((address.zip ?? '').length ? `, ${address.zip}` : '');
	const hasStreetAddress = Boolean(address.address) || Boolean(address.address2);
	const streetAddress = `${address.address ?? ''}${address.address2 ? `, ${address.address2}` : ''}`;

	return (
		<Container className="local-bootstrap">
			<Row className="pb-2">
				<Col xs={5}>
					Property / Company:
				</Col>
				<Col xs={7}>
					<b>
						{address.company_name}
					</b>
				</Col>
			</Row>
			<Row className="pb-2">
				<Col xs={5}>
					Name:
				</Col>
				<Col xs={7}>
					<b>
						{`${address.first_name ?? ''} ${address.last_name ?? ''}`}
					</b>
				</Col>
			</Row>
			<Row className="pb-2">
				<Col xs={5}>
					Address:
				</Col>
				<Col xs={7}>
					<b>
						{hasStreetAddress ? (
							streetAddress
						) : (
							<br />
						)}
					</b>
				</Col>
			</Row>
			<Row className="pb-2">
				<Col xs={5}>
					City:
				</Col>
				<Col xs={7}>
					<b>
						{cityString}
					</b>
				</Col>
			</Row>
			<Row className="pb-2">
				<Col xs={5}>
					Country:
				</Col>
				<Col xs={7}>
					<b>
						{address.country}
					</b>
				</Col>
			</Row>
			<Row className="pb-2">
				<Col xs={5}>
					Zip:
				</Col>
				<Col xs={7}>
					<b>
						{address.zip}
					</b>
				</Col>
			</Row>
			<Row>
				<Col className="text-end">
					<button
						type="button"
						className={`btn btn-sm green-button btn-${quoteType} text-white`}
						onClick={() => {
							onUseAddress(address);
						}}
					>
						Use
					</button>
				</Col>
			</Row>
		</Container>
	);
};

const ExistingAddresses = ({
	quoteType,
	getAddressesFunction,
	useAddress: applyAddress,
	createNewAddress,
	fullHeight = false,
}: ExistingAddressesProps) => {
	const [myAddresses, setMyAddresses] = useState<ExistingAddress[]>([]);
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		let isCancelled = false;

		const refreshAddresses = async () => {
			try {
				const resp = getAddressesFunction.includes('Shipping')
					? await apiCalls.getShippingAddresses(searchQuery, 15, 1)
					: await apiCalls.getPropertyAddresses(searchQuery, 15, 1);

				const responseData = resp.data as AddressListResponse;
				const filteredAddresses = (responseData.addresses ?? [])
					.filter((address) => Object.keys(address).some((key) => address[key as keyof ExistingAddress]))
					.filter((address) => (address.country_subdivision_id ?? 0) !== 0);

				const nonBrokenAddresses = filteredAddresses.map((address) => ({
					...address,
					address: address.address1 ?? address.address ?? '',
				}));

				nonBrokenAddresses.sort((addressA, addressB) => {
					if (addressA.creation_date && addressB.creation_date) {
						return new Date(addressB.creation_date).getTime() - new Date(addressA.creation_date).getTime();
					}
					if (addressA.creation_date) {
						return -1;
					}
					if (addressB.creation_date) {
						return 1;
					}
					return (addressB.id ?? 0) - (addressA.id ?? 0);
				});

				if (!isCancelled) {
					setMyAddresses(nonBrokenAddresses);
				}
			} catch (error) {
				if (!isCancelled) {
					ROOT.alert({
						quickFormat: 'error',
						text: 'Error loading addresses',
					});
				}
				throw new Error(`Error loading addresses ${String(error)}`);
			}
		};

		const timeoutId = window.setTimeout(() => {
			refreshAddresses();
		}, 1000);

		return () => {
			isCancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, [getAddressesFunction, searchQuery]);

	const handleUseAddress = async (address: ExistingAddress) => {
		if (!address.creation_date && typeof address.id === 'number') {
			try {
				const resp = await apiCalls.updateAddressCreationDate(address.id);
				const responseData = resp.data as AddressUpdateResponse;

				if (responseData.address) {
					applyAddress(responseData.address);
					return;
				}
			} catch (error) {
				console.error('Failed to update creation date', error);
			}
		}

		applyAddress(address);
	};

	return (
		<div>
			<div
				style={{
					width: '100%',
					display: 'flex',
					justifyContent: 'space-between',
					marginBottom: '5px',
					marginTop: '20px',
				}}
			>
				<h4 className={`text-${quoteType}`}>Existing Addresses</h4>
				<SearchBar
					quoteType={quoteType}
					style={searchBarStyle}
					onChange={(value: string) => {
						setSearchQuery(value.toLowerCase());
					}}
				/>
			</div>

			<div
				style={{
					height: fullHeight ? '500px' : '300px',
					overflow: 'auto',
					padding: '0 5px 0 0',
					marginTop: '8px',
				}}
			>
				{myAddresses.length > 0 ? myAddresses.map((address) => (
					<div
						key={hash(address)}
						style={{
							border: `1px solid ${Styles.psGray}`,
							borderRadius: '3px',
							marginBottom: '5px',
							padding: '1em',
							fontSize: '14px',
						}}
					>
						<ExistingAddressSummary
							quoteType={quoteType}
							address={address}
							onUseAddress={handleUseAddress}
						/>
					</div>
				)) : (
					<div style={{ color: Styles.psGray, marginTop: '20px' }}>
						<span style={{ fontSize: '20px' }}>No addresses to display.</span>
						<br />
						<span
							style={{
								color: Styles.psGray,
								display: 'inline-block',
								marginTop: '5px',
							}}
						>
							Would you like to
							{' '}
							<button
								type="button"
								className={`btn btn-link p-0 align-baseline text-${quoteType}`}
								style={{ textDecoration: 'underline' }}
								onClick={createNewAddress}
							>
								create a new address
							</button>
							?
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default ExistingAddresses;
